import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Users, DollarSign, Film, ShoppingBag, Calendar, MessageSquare, 
  CreditCard, Gift, LogOut, Home, Plus, Trash2, Edit, Eye, EyeOff,
  Loader2, Save, X, Check, User, Camera, UsersRound, Building2, Megaphone, FileText, TrendingUp, UserCog, Star, Trophy, Bell, Image as ImageIcon
} from "lucide-react";

// Sub-components
import PlayersManager from "@/components/admin/PlayersManager";
import PlayerProgressManager from "@/components/admin/PlayerProgressManager";
import CoachesManager from "@/components/admin/CoachesManager";
import SpecialGuestsManager from "@/components/admin/SpecialGuestsManager";
import SponsorsManager from "@/components/admin/SponsorsManager";
import SponsorApplicationsManager from "@/components/admin/SponsorApplicationsManager";
import AnnouncementsManager from "@/components/admin/AnnouncementsManager";
import PricingManager from "@/components/admin/PricingManager";
import MediaManager from "@/components/admin/MediaManager";
import MerchManager from "@/components/admin/MerchManager";
import ScheduleManager from "@/components/admin/ScheduleManager";
import CommentsManager from "@/components/admin/CommentsManager";
import TransactionsView from "@/components/admin/TransactionsView";
import ReferralAnalytics from "@/components/admin/ReferralAnalytics";
import AboutManager from "@/components/admin/AboutManager";
import PhotosManager from "@/components/admin/PhotosManager";
import UsersManager from "@/components/admin/UsersManager";
import LeaderboardManager from "@/components/admin/LeaderboardManager";
import HSFootballManager from "@/components/admin/HSFootballManager";
import AlertsManager from "@/components/admin/AlertsManager";
import GalleryManager from "@/components/admin/GalleryManager";

const AdminDashboard = () => {
  const { user, token, logout, authAxios, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'photos');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    players: 0,
    plans: 0,
    media: 0,
    merch: 0,
    schedules: 0,
    comments: 0
  });

  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) return;
    
    if (!token) {
      navigate('/');
      toast.error("Please login first");
      return;
    }

    // Check if user is admin
    if (user && user.role !== 'admin') {
      navigate('/');
      toast.error("Admin access required");
      return;
    }

    // If we have token but user not loaded yet, wait
    if (token && !user) {
      return;
    }

    fetchStats();
  }, [token, user, authLoading]);

  const fetchStats = async () => {
    try {
      const [players, plans, media, merch, schedules, comments] = await Promise.all([
        axios.get(`${API}/players`),
        axios.get(`${API}/pricing`),
        axios.get(`${API}/media`),
        axios.get(`${API}/merchandise`),
        axios.get(`${API}/schedule`),
        axios.get(`${API}/comments`)
      ]);

      setStats({
        players: players.data.length,
        plans: plans.data.length,
        media: media.data.length,
        merch: merch.data.length,
        schedules: schedules.data.length,
        comments: comments.data.length
      });
    } catch (e) {
      console.error("Error fetching stats:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-void-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-neon-blue animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-void-black">
      {/* Header */}
      <header className="bg-void-black border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <div className="font-graffiti text-xl">
                <span className="neon-text-blue">GRIND</span>
                <span className="neon-text-pink">TIME</span>
              </div>
              <span className="font-orbitron text-xs text-gray-500 uppercase tracking-wider">
                Admin Dashboard
              </span>
            </div>

            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/')}
                className="flex items-center gap-2 px-3 py-1.5 text-gray-400 hover:text-neon-blue transition-colors font-rajdhani text-sm"
              >
                <Home className="w-4 h-4" />
                View Site
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-3 py-1.5 text-gray-400 hover:text-neon-pink transition-colors font-rajdhani text-sm"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Stats Overview */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <StatCard icon={Users} label="Players" value={stats.players} color="blue" />
          <StatCard icon={DollarSign} label="Plans" value={stats.plans} color="green" />
          <StatCard icon={Film} label="Media" value={stats.media} color="pink" />
          <StatCard icon={ShoppingBag} label="Products" value={stats.merch} color="yellow" />
          <StatCard icon={Calendar} label="Sessions" value={stats.schedules} color="cyan" />
          <StatCard icon={MessageSquare} label="Comments" value={stats.comments} color="purple" />
        </div>

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full flex flex-wrap bg-void-black border border-white/10 p-1 mb-6 gap-0.5">
            <TabsTrigger value="photos" className="flex-1 min-w-[70px] px-2 py-1.5 font-orbitron text-[10px] data-[state=active]:bg-gradient-to-r data-[state=active]:from-neon-blue data-[state=active]:to-neon-pink data-[state=active]:text-white">
              <Camera className="w-3 h-3 mr-1" />
              Photos
            </TabsTrigger>
            <TabsTrigger value="announcements" className="flex-1 min-w-[70px] px-2 py-1.5 font-orbitron text-[10px] data-[state=active]:bg-indigo-500 data-[state=active]:text-white">
              <Megaphone className="w-3 h-3 mr-1" />
              Messages
            </TabsTrigger>
            <TabsTrigger value="players" className="flex-1 min-w-[70px] px-2 py-1.5 font-orbitron text-[10px] data-[state=active]:bg-neon-blue data-[state=active]:text-black">
              <Users className="w-3 h-3 mr-1" />
              Players
            </TabsTrigger>
            <TabsTrigger value="progress" className="flex-1 min-w-[70px] px-2 py-1.5 font-orbitron text-[10px] data-[state=active]:bg-emerald-500 data-[state=active]:text-black">
              <TrendingUp className="w-3 h-3 mr-1" />
              Progress
            </TabsTrigger>
            <TabsTrigger value="coaches" className="flex-1 min-w-[70px] px-2 py-1.5 font-orbitron text-[10px] data-[state=active]:bg-orange-500 data-[state=active]:text-black">
              <UsersRound className="w-3 h-3 mr-1" />
              Coaches
            </TabsTrigger>
            <TabsTrigger value="guests" className="flex-1 min-w-[70px] px-2 py-1.5 font-orbitron text-[10px] data-[state=active]:bg-gradient-to-r data-[state=active]:from-neon-blue data-[state=active]:to-neon-pink data-[state=active]:text-white">
              <Star className="w-3 h-3 mr-1" />
              Guests
            </TabsTrigger>
            <TabsTrigger value="pricing" className="flex-1 min-w-[70px] px-2 py-1.5 font-orbitron text-[10px] data-[state=active]:bg-neon-green data-[state=active]:text-black">
              <DollarSign className="w-3 h-3 mr-1" />
              Pricing
            </TabsTrigger>
            <TabsTrigger value="media" className="flex-1 min-w-[70px] px-2 py-1.5 font-orbitron text-[10px] data-[state=active]:bg-neon-pink data-[state=active]:text-black">
              <Film className="w-3 h-3 mr-1" />
              Media
            </TabsTrigger>
            <TabsTrigger value="gallery" className="flex-1 min-w-[70px] px-2 py-1.5 font-orbitron text-[10px] data-[state=active]:bg-gradient-to-r data-[state=active]:from-pink-500 data-[state=active]:to-purple-500 data-[state=active]:text-white">
              <ImageIcon className="w-3 h-3 mr-1" />
              Gallery
            </TabsTrigger>
            <TabsTrigger value="merch" className="flex-1 min-w-[70px] px-2 py-1.5 font-orbitron text-[10px] data-[state=active]:bg-yellow-500 data-[state=active]:text-black">
              <ShoppingBag className="w-3 h-3 mr-1" />
              Merch
            </TabsTrigger>
            <TabsTrigger value="sponsors" className="flex-1 min-w-[70px] px-2 py-1.5 font-orbitron text-[10px] data-[state=active]:bg-cyan-400 data-[state=active]:text-black">
              <Building2 className="w-3 h-3 mr-1" />
              Sponsors
            </TabsTrigger>
            <TabsTrigger value="applications" className="flex-1 min-w-[70px] px-2 py-1.5 font-orbitron text-[10px] data-[state=active]:bg-pink-500 data-[state=active]:text-white">
              <FileText className="w-3 h-3 mr-1" />
              Apps
            </TabsTrigger>
            <TabsTrigger value="schedule" className="flex-1 min-w-[70px] px-2 py-1.5 font-orbitron text-[10px] data-[state=active]:bg-teal-500 data-[state=active]:text-black">
              <Calendar className="w-3 h-3 mr-1" />
              Schedule
            </TabsTrigger>
            <TabsTrigger value="comments" className="flex-1 min-w-[70px] px-2 py-1.5 font-orbitron text-[10px] data-[state=active]:bg-purple-500 data-[state=active]:text-white">
              <MessageSquare className="w-3 h-3 mr-1" />
              Comments
            </TabsTrigger>
            <TabsTrigger value="users" className="flex-1 min-w-[70px] px-2 py-1.5 font-orbitron text-[10px] data-[state=active]:bg-sky-500 data-[state=active]:text-white">
              <UserCog className="w-3 h-3 mr-1" />
              Users
            </TabsTrigger>
            <TabsTrigger value="leaderboard" className="flex-1 min-w-[70px] px-2 py-1.5 font-orbitron text-[10px] data-[state=active]:bg-gradient-to-r data-[state=active]:from-neon-pink data-[state=active]:to-purple-600 data-[state=active]:text-white">
              <Trophy className="w-3 h-3 mr-1" />
              Leaders
            </TabsTrigger>
            <TabsTrigger value="hs-football" className="flex-1 min-w-[70px] px-2 py-1.5 font-orbitron text-[10px] data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500 data-[state=active]:to-yellow-500 data-[state=active]:text-white">
              <Trophy className="w-3 h-3 mr-1" />
              HS FB
            </TabsTrigger>
            <TabsTrigger value="about" className="flex-1 min-w-[70px] px-2 py-1.5 font-orbitron text-[10px] data-[state=active]:bg-rose-500 data-[state=active]:text-white">
              <User className="w-3 h-3 mr-1" />
              About
            </TabsTrigger>
            <TabsTrigger value="transactions" className="flex-1 min-w-[70px] px-2 py-1.5 font-orbitron text-[10px] data-[state=active]:bg-gradient-to-r data-[state=active]:from-green-500 data-[state=active]:to-emerald-600 data-[state=active]:text-white">
              <CreditCard className="w-3 h-3 mr-1" />
              Revenue
            </TabsTrigger>
            <TabsTrigger value="referrals" className="flex-1 min-w-[70px] px-2 py-1.5 font-orbitron text-[10px] data-[state=active]:bg-gradient-to-r data-[state=active]:from-amber-500 data-[state=active]:to-orange-600 data-[state=active]:text-white">
              <Gift className="w-3 h-3 mr-1" />
              Referrals
            </TabsTrigger>
            <TabsTrigger value="alerts" className="flex-1 min-w-[70px] px-2 py-1.5 font-orbitron text-[10px] data-[state=active]:bg-gradient-to-r data-[state=active]:from-red-500 data-[state=active]:to-yellow-500 data-[state=active]:text-white">
              <Bell className="w-3 h-3 mr-1" />
              Alerts
            </TabsTrigger>
          </TabsList>

          <div className="mt-12 pt-4">
          <TabsContent value="photos">
            <PhotosManager />
          </TabsContent>
          <TabsContent value="announcements">
            <AnnouncementsManager />
          </TabsContent>
          <TabsContent value="players">
            <PlayersManager />
          </TabsContent>
          <TabsContent value="progress">
            <PlayerProgressManager />
          </TabsContent>
          <TabsContent value="coaches">
            <CoachesManager />
          </TabsContent>
          <TabsContent value="guests">
            <SpecialGuestsManager />
          </TabsContent>
          <TabsContent value="pricing">
            <PricingManager />
          </TabsContent>
          <TabsContent value="media">
            <MediaManager />
          </TabsContent>
          <TabsContent value="gallery">
            <GalleryManager />
          </TabsContent>
          <TabsContent value="merch">
            <MerchManager />
          </TabsContent>
          <TabsContent value="sponsors">
            <SponsorsManager />
          </TabsContent>
          <TabsContent value="applications">
            <SponsorApplicationsManager />
          </TabsContent>
          <TabsContent value="schedule">
            <ScheduleManager />
          </TabsContent>
          <TabsContent value="comments">
            <CommentsManager />
          </TabsContent>
          <TabsContent value="users">
            <UsersManager />
          </TabsContent>
          <TabsContent value="leaderboard">
            <LeaderboardManager />
          </TabsContent>
          <TabsContent value="hs-football">
            <HSFootballManager />
          </TabsContent>
          <TabsContent value="referrals">
            <ReferralAnalytics />
          </TabsContent>
          <TabsContent value="about">
            <AboutManager />
          </TabsContent>
          <TabsContent value="transactions">
            <TransactionsView />
          </TabsContent>
          <TabsContent value="alerts">
            <AlertsManager />
          </TabsContent>
          </div>
        </Tabs>
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, color }) => {
  const colorClasses = {
    blue: "text-neon-blue border-neon-blue/30",
    pink: "text-neon-pink border-neon-pink/30",
    green: "text-neon-green border-neon-green/30",
    yellow: "text-yellow-400 border-yellow-400/30",
    cyan: "text-cyan-400 border-cyan-400/30",
    purple: "text-purple-400 border-purple-400/30"
  };

  return (
    <div className={`cyber-card p-4 border ${colorClasses[color]}`}>
      <Icon className={`w-5 h-5 mb-2 ${colorClasses[color].split(' ')[0]}`} />
      <div className="font-orbitron text-2xl text-white">{value}</div>
      <div className="font-rajdhani text-xs text-gray-500 uppercase">{label}</div>
    </div>
  );
};

export default AdminDashboard;
